import { Texture } from "pixi.js"
import { wallArtAtlas } from "../sprites/canvas_atlas"
import { bindContent } from "game-common/content/catalog"
import { TileSize } from "game-common/models"
import { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import { autotileIndexBitmask, S } from "game-common/room/autotile_bitmask"
import { grassAt } from "./grass_feet"
import { RootEdge, rootFringeBlades, treeRootContour } from "./tree_root_fringe"

// Wall definitions distinguish bases on the anchor row from raised caps. Rebuild this
// small lookup when content changes, rather than examining rules for every wall.
let wallBases = new Set<string>()
let southConnectedBases = new Set<string>()
bindContent(catalog => catalog.source.autotiles, file => {
    wallBases = new Set()
    southConnectedBases = new Set()
    const visit = (node: any, tileset: string, southConnected: boolean) => {
        if (!node || typeof node !== "object") return
        if (typeof node.tile === "number" && !(node.dy || 0)) {
            const id = `${tileset}-${node.tile + 1}`
            wallBases.add(id)
            if (southConnected) southConnectedBases.add(id)
        }
        Object.values(node).forEach(value => visit(value, tileset, southConnected))
    }
    file.definitions.forEach((definition: any) => {
        if (definition.kind !== "wall") return
        Object.entries(definition.rules).forEach(([index, rule]) => {
            // Index 14 connects north and south: its bottom is a join to the next
            // wall segment, rather than an exposed foot meeting the grass.
            if (index !== "14") visit(rule, definition.tilesetId, !!(autotileIndexBitmask(Number(index)) & S))
        })
    })
})

export const isWallGrassBase = (id: string, x: number, y: number,
    parent?: { x: number; y: number }): boolean =>
    wallBases.has(id) && (!parent || (parent.x === x && parent.y === y))

const baked = new WeakMap<Texture, Texture>()

/** Corners and junctions can have both an exposed horizontal foot and a vertical join. */
export const wallGrassEdges = (id: string, edges: RootEdge[], height: number): RootEdge[] =>
    southConnectedBases.has(id) ? edges.filter(edge => edge.y < height - 1) : edges

/** One terrain check on placement/redraw, one image bake per wall variant, no frame-loop work. */
export const wallGrassFringeTexture = (wall: Texture, id: string, x: number, y: number,
    provider: TileMapMetaProvider, parent?: { x: number; y: number }): Texture => {
    if (!isWallGrassBase(id, x, y, parent) || !grassAt(provider, x * TileSize + 16, y * TileSize + 16)) return wall
    const cached = baked.get(wall)
    if (cached && !cached.baseTexture.destroyed) return cached
    const source = (wall.baseTexture.resource as any)?.source
    if (!source) return wall
    const canvas = document.createElement("canvas")
    canvas.width = wall.width
    canvas.height = wall.height
    const context = canvas.getContext("2d")
    if (!context) return wall
    const frame = wall.frame, trim = wall.trim
    context.drawImage(source, frame.x, frame.y, frame.width, frame.height,
        trim?.x || 0, trim?.y || 0, trim?.width || wall.width, trim?.height || wall.height)
    const edges = treeRootContour(context.getImageData(0, 0, canvas.width, canvas.height).data, canvas.width, canvas.height)
    for (const blade of rootFringeBlades(wallGrassEdges(id, edges, canvas.height))) {
        context.fillStyle = `#${blade.color.toString(16).padStart(6, "0")}`
        context.fillRect(blade.x, blade.y - blade.height + 1, 1, blade.height)
        context.fillRect(blade.x + (blade.x % 4 ? -1 : 1), blade.y - blade.height, 1, 1)
    }
    // A canvas-backed texture keeps the existing damage/repair system's image access intact.
    // On a shared page (wallArtAtlas), so a wall of distinct cells batches together.
    const texture = wallArtAtlas.putCanvas(canvas) ?? Texture.from(canvas)
    baked.set(wall, texture)
    return texture
}
